import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import StatusBadge from '../components/StatusBadge';
import Timeline from '../components/Timeline';
import SeverityBadge from '../components/SeverityBadge';
import DocumentChecklist from '../components/DocumentChecklist';
import { API, aiApi, fileApi } from '../lib/api';
import { toast } from 'sonner';
import { FileText, Download, Sparkles } from 'lucide-react';

export default function TrackCasePage() {
  const location = useLocation();
  const [caseNumber, setCaseNumber] = useState(location.state?.case_number || '');
  const [pin, setPin] = useState(location.state?.pin || '');
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const fetchCase = async (cn, p) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/track/${encodeURIComponent(cn)}/${encodeURIComponent(p)}`);
      setCaseData(data);
      toast.success('Fall geladen');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fall nicht gefunden');
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.case_number && location.state?.pin) {
      fetchCase(location.state.case_number, location.state.pin);
    }
  }, []);

  const runAI = async () => {
    if (!caseData) return;
    setAiLoading(true);
    try {
      const { data } = await API.post(`/ai/report/${caseData.id}`);
      setCaseData((c) => ({ ...c, ai_analysis: data.analysis, ai_report: data.report, ai_insurance_summary: data.insurance_summary }));
      toast.success('KI-Analyse abgeschlossen');
    } catch (e) {
      toast.error('KI-Analyse fehlgeschlagen');
    } finally {
      setAiLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!caseData) return;
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ai/pdf/${caseData.id}`;
      window.open(url, '_blank');
    } catch (e) {
      toast.error('PDF konnte nicht erstellt werden');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <h1 className="font-heading font-semibold text-2xl sm:text-3xl text-[hsl(var(--primary))]">Status verfolgen</h1>
        <p className="text-sm text-slate-600">Geben Sie Ihre Fallnummer und die PIN ein, die Sie nach der Meldung erhalten haben.</p>

        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5 grid sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label>Fallnummer</Label>
              <Input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} placeholder="UH-..." className="h-12 rounded-xl mt-1" data-testid="track-case-number-input" />
            </div>
            <div>
              <Label>PIN</Label>
              <Input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="6-stellige PIN" className="h-12 rounded-xl mt-1" data-testid="track-pin-input" />
            </div>
            <Button onClick={() => fetchCase(caseNumber, pin)} disabled={loading || !caseNumber || !pin} className="h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" data-testid="track-submit-button">
              {loading ? 'Lade...' : 'Status anzeigen'}
            </Button>
          </CardContent>
        </Card>

        {caseData && (
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <div className="text-xs text-slate-500">Fallnummer</div>
                      <div className="font-heading font-semibold text-lg text-[hsl(var(--primary))]">{caseData.case_number}</div>
                    </div>
                    <StatusBadge status={caseData.status} testid="track-case-status" />
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Kunde:</span> {caseData.person.first_name} {caseData.person.last_name}</div>
                    <div><span className="text-slate-500">Telefon:</span> {caseData.person.phone}</div>
                    <div><span className="text-slate-500">Fahrzeug:</span> {caseData.vehicle.make} {caseData.vehicle.model}</div>
                    <div><span className="text-slate-500">Kennzeichen:</span> {caseData.vehicle.license_plate}</div>
                    <div><span className="text-slate-500">Datum:</span> {caseData.accident.date} {caseData.accident.time}</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="font-heading font-semibold text-slate-900 mb-4">Status-Timeline</div>
                  <Timeline steps={caseData.timeline} dataTestId="track-timeline" />
                </CardContent>
              </Card>

              {caseData.photos?.length > 0 && (
                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="p-5">
                    <div className="font-heading font-semibold text-slate-900 mb-3">Hochgeladene Fotos</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {caseData.photos.map((id) => (
                        <img key={id} src={fileApi.url(id)} alt="Unfall" className="w-full h-28 object-cover rounded-xl border border-slate-200" />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-4">
              {caseData.id && <DocumentChecklist caseId={caseData.id} testid="track-doc-checklist" />}
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5 text-[hsl(var(--accent))]" />
                    <div className="font-heading font-semibold text-slate-900">KI-Ersteinschätzung</div>
                  </div>
                  {caseData.ai_analysis ? (
                    <div className="text-sm space-y-2">
                      <SeverityBadge ampel={caseData.ai_analysis.ampel} ampelLabel={caseData.ai_analysis.ampel_label} gesamt={caseData.ai_analysis.gesamt_einschaetzung} size="lg" testid="ai-severity-badge" />
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        <div className="text-slate-500">Gesamteinschätzung:</div>
                        <div className="font-medium">{caseData.ai_analysis.gesamt_einschaetzung || '-'}</div>
                        <div className="text-slate-500">Fahrbereit:</div>
                        <div className="font-medium">{caseData.ai_analysis.fahrbereit_einschaetzung || '-'}</div>
                      </div>
                      <div className="pt-2">
                        <div className="font-medium text-slate-900 mb-1">Erkannte Schäden</div>
                        <ul className="space-y-1">
                          {(caseData.ai_analysis.schaeden || []).map((d, i) => (
                            <li key={i} className="text-slate-700">• <span className="font-medium">{d.bauteil}</span> — {d.schweregrad} ({Math.round((d.konfidenz || 0) * 100)}%)</li>
                          ))}
                          {(!caseData.ai_analysis.schaeden || caseData.ai_analysis.schaeden.length === 0) && (
                            <li className="text-slate-500">Keine Schäden eindeutig erkannt.</li>
                          )}
                        </ul>
                      </div>
                      {caseData.ai_analysis.empfehlung && (
                        <div className="pt-2 text-slate-700"><span className="font-medium">Empfehlung:</span> {caseData.ai_analysis.empfehlung}</div>
                      )}
                      <div className="text-[11px] text-slate-500 pt-2">{caseData.ai_analysis.disclaimer}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-600">Noch keine Analyse vorhanden.</div>
                  )}
                  <div className="mt-4 flex flex-col gap-2">
                    <Button onClick={runAI} disabled={aiLoading || (caseData.photos || []).length === 0} className="bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" data-testid="track-run-ai-button">
                      <Sparkles className="h-4 w-4 mr-1.5" /> {aiLoading ? 'Analysiere...' : 'KI-Analyse starten'}
                    </Button>
                    <Button variant="outline" onClick={downloadPdf} data-testid="track-download-pdf-button">
                      <Download className="h-4 w-4 mr-1.5" /> Bericht als PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
